import { forwardRef } from "react";
import * as HoverCardPrimitive from "@radix-ui/react-hover-card";
import { cn } from "../lib/cn";

/**
 * HoverCard — ledger row preview on @radix-ui/react-hover-card.
 *
 *   <HoverCard>
 *     <HoverCardTrigger asChild><LedgerRow … /></HoverCardTrigger>
 *     <HoverCardContent>
 *       <div className="flex items-baseline gap-2">
 *         <HoverCardTitle>Decking — Plot 12</HoverCardTitle>
 *         <HoverCardKind>Labour item</HoverCardKind>
 *       </div>
 *       <HoverCardDescription>Truncated note reappears here…</HoverCardDescription>
 *       <HoverCardFacts>
 *         <HoverCardFact label="Units">7.5 hrs</HoverCardFact>
 *         <HoverCardFact label="Rate">£18.00</HoverCardFact>
 *       </HoverCardFacts>
 *     </HoverCardContent>
 *   </HoverCard>
 *
 * Modelled on the portal's LedgerHoverCard: a shortcut to the side panel
 * where demoted facts reappear. Which facts exist is the caller's call.
 * `HoverCardTrigger` is an unstyled passthrough — wrap the row with
 * `asChild`.
 */

export interface HoverCardProps
  extends React.ComponentPropsWithoutRef<typeof HoverCardPrimitive.Root> {}

/** Controlled/uncontrolled root. Renders no DOM element. */
export function HoverCard({
  openDelay = 1000,
  closeDelay = 300,
  ...props
}: HoverCardProps) {
  return (
    <HoverCardPrimitive.Root
      openDelay={openDelay}
      closeDelay={closeDelay}
      {...props}
    />
  );
}
HoverCard.displayName = "HoverCard";

export interface HoverCardTriggerProps
  extends React.ComponentPropsWithoutRef<typeof HoverCardPrimitive.Trigger> {}

/** Unstyled anchor — use `asChild` around the hovered element. */
export const HoverCardTrigger = forwardRef<
  React.ComponentRef<typeof HoverCardPrimitive.Trigger>,
  HoverCardTriggerProps
>(({ className, ...props }, ref) => {
  return (
    <HoverCardPrimitive.Trigger ref={ref} className={className} {...props} />
  );
});
HoverCardTrigger.displayName = "HoverCardTrigger";

export interface HoverCardContentProps
  extends React.ComponentPropsWithoutRef<typeof HoverCardPrimitive.Content> {}

/**
 * Portalled preview panel — docks beside the row (right-start default,
 * away from the pointer so the card is not hovered itself). Same radius,
 * border and elevation grammar as the Popover panel.
 */
export const HoverCardContent = forwardRef<
  React.ComponentRef<typeof HoverCardPrimitive.Content>,
  HoverCardContentProps
>(
  (
    { className, side = "right", align = "start", sideOffset = 6, ...props },
    ref,
  ) => {
    return (
      <HoverCardPrimitive.Portal>
        <HoverCardPrimitive.Content
          ref={ref}
          side={side}
          align={align}
          sideOffset={sideOffset}
          collisionPadding={8}
          className={cn(
            "z-(--z-popover) w-max min-w-[230px] max-w-[300px] rounded-lg border border-border-default bg-surface-raised p-3 shadow-popover outline-none",
            className,
          )}
          {...props}
        />
      </HoverCardPrimitive.Portal>
    );
  },
);
HoverCardContent.displayName = "HoverCardContent";

/** Row heading — the entity the card previews. */
export const HoverCardTitle = forwardRef<
  HTMLSpanElement,
  React.HTMLAttributes<HTMLSpanElement>
>(({ className, ...props }, ref) => {
  return (
    <span
      ref={ref}
      className={cn(
        "type-body-md min-w-0 font-semibold text-content-primary",
        className,
      )}
      {...props}
    />
  );
});
HoverCardTitle.displayName = "HoverCardTitle";

/** Quiet small-caps tag beside the title — "Labour item", "Deduction". */
export const HoverCardKind = forwardRef<
  HTMLSpanElement,
  React.HTMLAttributes<HTMLSpanElement>
>(({ className, ...props }, ref) => {
  return (
    <span
      ref={ref}
      className={cn("type-overline shrink-0 text-content-secondary", className)}
      {...props}
    />
  );
});
HoverCardKind.displayName = "HoverCardKind";

/** Free text under the heading — where a demoted description reappears. */
export const HoverCardDescription = forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
  return (
    <p
      ref={ref}
      className={cn("type-caption mt-1 text-content-secondary", className)}
      {...props}
    />
  );
});
HoverCardDescription.displayName = "HoverCardDescription";

export interface HoverCardFactsProps
  extends React.HTMLAttributes<HTMLDListElement> {}

/** Stack of label/value rows — divided off the prose above. */
export const HoverCardFacts = forwardRef<HTMLDListElement, HoverCardFactsProps>(
  ({ className, ...props }, ref) => {
    return (
      <dl
        ref={ref}
        className={cn(
          "mt-2 flex flex-col gap-0.5 border-t border-border-default pt-2",
          className,
        )}
        {...props}
      />
    );
  },
);
HoverCardFacts.displayName = "HoverCardFacts";

export interface HoverCardFactProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** Fact label — muted, never shrinks away. */
  label: React.ReactNode;
  children: React.ReactNode;
}

/** One label/value pair in a hover card. Every fact is the caller's choice. */
export const HoverCardFact = forwardRef<HTMLDivElement, HoverCardFactProps>(
  ({ label, className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          "flex items-baseline justify-between gap-4 py-0.5",
          className,
        )}
        {...props}
      >
        <dt className="type-caption shrink-0 text-content-secondary">
          {label}
        </dt>
        <dd className="type-caption text-right tabular-nums text-content-primary">
          {children}
        </dd>
      </div>
    );
  },
);
HoverCardFact.displayName = "HoverCardFact";
